import { Link } from '@remix-run/react';
import { useMemo } from 'react';
import { Trans } from 'react-i18next';

import {
  CheckCircleIcon,
  ChevronDownIcon,
  ComputerDesktopIcon,
  MoonIcon,
  PaintBrushIcon,
  SunIcon,
} from '@heroicons/react/24/outline';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '~/core/ui/Dropdown';

import ProfileAvatar from '~/components/ProfileAvatar';
import configuration from '~/configuration';
import type UserSession from '~/core/session/types/user-session';

import {
  DARK_THEME_CLASSNAME,
  LIGHT_THEME_CLASSNAME,
  SYSTEM_THEME_CLASSNAME,
  getStoredTheme,
  setTheme,
} from '~/core/theming';

import useUser from '~/core/hooks/use-user';
import GlobalRole from '~/core/session/types/global-role';
import Icon from '~/core/ui/Icon';
import If from '~/core/ui/If';
import DarkModeToggle from './DarkModeToggle';

const ProfileDropdown: React.FCC<{
  userSession: Maybe<UserSession>;
  signOutRequested: () => unknown;
  className?: string;
  avatarClassName?: string;
  displayName?: boolean;
  noIcon?: boolean;
  style?: React.CSSProperties;
}> = ({
  userSession,
  signOutRequested,
  className,
  avatarClassName,
  displayName = true,
  noIcon = false,
  style,
}) => {
  const { data: user } = useUser({
    enabled: true,
    refetchOnMount: false,
  });

  const userDisplayName = userSession?.data?.displayName;

  const signedInAsLabel = useMemo(() => {
    const auth = userSession?.auth;
    const email = auth?.email;
    const phone = auth?.phone;

    return userDisplayName || email || phone;
  }, [userSession, userDisplayName]);

  const isSuperAdmin = useMemo(() => {
    return user?.app_metadata.role === GlobalRole.SuperAdmin;
  }, [user]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Open your profile menu"
        className={
          'flex cursor-pointer items-center space-x-3 focus:outline-none justify-between hover:bg-gray-50 border-transparent dark:hover:bg-dark-900/50 dark:hover:text-white rounded-md focus:ring-2 focus:ring-secondary-500 focus:ring-opacity-50 hover:ring-2 hover:ring-secondary-500 hover:ring-opacity-50  ' +
          className
        }
        style={style}
      >
        <span
          className={`${displayName ? 'flex p-0.5' : 'block p-0'} items-center rounded-full gap-2 transition-shadow duration-200`}
        >
          <ProfileAvatar
            user={userSession}
            className={'w-[30px] h-[30px] rounded-[4rem] ' + avatarClassName}
          />
          {displayName && (
            <span className={'text-sm font-medium'}>{signedInAsLabel}</span>
          )}
        </span>
        {!noIcon && <ChevronDownIcon className={'h-3 block'} />}
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className={'!min-w-[15rem] z-[99]'}
        collisionPadding={{ left: 25, right: 25 }}
        sideOffset={20}
      >
        <DropdownMenuItem className={'!h-12 rounded-none'}>
          <div
            className={
              'flex flex-col justify-start truncate text-left text-xs gap-[0.25rem]'
            }
          >
            <div className={'text-gray-500'}>Signed in as</div>

            <div>
              <span className={'block truncate'}>{signedInAsLabel}</span>
            </div>
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* <DropdownMenuItem className="flex flex-col md:hidden !items-start h-[4.25rem] gap-[0.25rem]">
          <div className={'text-gray-500 text-xs'}>Organization</div>
          <OrganizationsSelector className="flex md:hidden max-w-[231px] !ml-0" />{' '}
        </DropdownMenuItem>

        <DropdownMenuSeparator className="flex md:hidden" /> */}

        <DropdownMenuItem>
          <Link
            className={'flex h-full w-full items-center space-x-3'}
            to={configuration.paths.appHome}
          >
            <Icon
              icon="solar:chat-dots-bold"
              width="1.25rem"
              className="flex items-center"
            />
            <span>
              <Trans i18nKey={'common:dashboardTabLabel'} />
            </span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link
            className={'flex items-center space-x-3'}
            to={'/settings/profile'}
          >
            <Icon
              icon="solar:settings-bold"
              width="1.25rem"
              className="flex items-center"
            />
            <span>
              <Trans i18nKey={'common:settingsTabLabel'} />
            </span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link className={'flex items-center space-x-3'} to={'/faq'}>
            <Icon
              icon="mingcute:question-fill"
              width="1.25rem"
              className="flex items-center"
            />
            <span>
              <Trans i18nKey={'common:faqTabLabel'} />
            </span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link className={'flex items-center space-x-3'} to={'/blog'}>
            <Icon
              icon="mingcute:edit-4-fill"
              width="1.25rem"
              className="flex items-center"
            />
            <span>
              <Trans i18nKey={'common:blogTabLabel'} />
            </span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link className={'flex items-center space-x-3'} to={'/tools'}>
            <Icon
              icon="mdi:tools"
              width="1.25rem"
              className="flex items-center"
            />
            <span>Tools</span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link
            className={'flex items-center space-x-3'}
            to={'/bible-characters'}
          >
            <Icon
              icon="fluent:person-standing-16-filled"
              width="1.25rem"
              className="flex items-center"
            />
            <span>Bible Characters</span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link className={'flex items-center space-x-3'} to={'/'}>
            <Icon
              icon="ant-design:home-filled"
              width="1rem"
              className="flex items-center mx-0.5"
            />
            <span>Home Page</span>
          </Link>
        </DropdownMenuItem>

        <span className="hidden sm:block">
          <ThemeSelectorSubMenu />
        </span>
        <span className="sm:hidden">
          <DropdownMenuSeparator />
          <DarkModeToggle showMobileLabel />
        </span>

        <DropdownMenuSeparator />

        <If condition={isSuperAdmin}>
          <DropdownMenuItem asChild>
            <Link className={'flex items-center space-x-3'} to={'/admin'}>
              <Icon
                icon="mingcute:building-1-fill"
                width="1.25rem"
                className="flex items-center"
              />

              <span>Admin</span>
            </Link>
          </DropdownMenuItem>

          <DropdownMenuSeparator />
        </If>

        <DropdownMenuItem
          role={'button'}
          className={'cursor-pointer'}
          onClick={signOutRequested}
        >
          <span className={'flex w-full items-center space-x-3'}>
            <Icon
              icon="mingcute:exit-fill"
              width="1.25rem"
              className="flex items-center"
            />

            <span>
              <Trans i18nKey={'auth:signOut'} />
            </span>
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

function ThemeSelectorSubMenu() {
  const currentTheme = useMemo(() => getStoredTheme(), []);

  const Wrapper: React.FCC = ({ children }) => (
    <span className={'flex items-center space-x-3.5'}>{children}</span>
  );

  return (
    <>
      <DropdownMenuSeparator className={'flex'} />

      <DropdownMenuSub>
        <DropdownMenuSubTrigger className={'flex'}>
          <Wrapper>
            <PaintBrushIcon className={'h-5'} />

            <span>
              <Trans i18nKey={'common:theme'} />
            </span>
          </Wrapper>
        </DropdownMenuSubTrigger>

        <DropdownMenuSubContent>
          <DropdownMenuItem
            className={'cursor-pointer flex justify-between items-center'}
            onClick={() => setTheme(LIGHT_THEME_CLASSNAME)}
          >
            <Wrapper>
              <SunIcon className={'h-4'} />

              <span>
                <Trans i18nKey={'common:lightTheme'} />
              </span>
            </Wrapper>

            <If condition={currentTheme === LIGHT_THEME_CLASSNAME}>
              <CheckCircleIcon className={'h-5'} />
            </If>
          </DropdownMenuItem>

          <DropdownMenuItem
            className={'cursor-pointer flex justify-between items-center'}
            onClick={() => setTheme(DARK_THEME_CLASSNAME)}
          >
            <Wrapper>
              <MoonIcon className={'h-4'} />

              <span>
                <Trans i18nKey={'common:darkTheme'} />
              </span>
            </Wrapper>

            <If condition={currentTheme === DARK_THEME_CLASSNAME}>
              <CheckCircleIcon className={'h-5'} />
            </If>
          </DropdownMenuItem>

          <DropdownMenuItem
            className={'cursor-pointer flex justify-between items-center'}
            onClick={() => setTheme(SYSTEM_THEME_CLASSNAME)}
          >
            <Wrapper>
              <ComputerDesktopIcon className={'h-4'} />

              <span>
                <Trans i18nKey={'common:systemTheme'} />
              </span>
            </Wrapper>

            <If condition={currentTheme === SYSTEM_THEME_CLASSNAME}>
              <CheckCircleIcon className={'h-5'} />
            </If>
          </DropdownMenuItem>
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    </>
  );
}

export default ProfileDropdown;
